import { FC } from "react";
import { Section } from "react-migration/components/Section";
import { SupportedLocale } from "src/js/util/dates";
import { MeasurementSystem } from "src/js/stores/user/store";
import { ZooplaComparable } from "react-migration/lib/typings/Zoopla";
import { getZooplaAccessLevel } from "react-migration/domains/comparables/util/getZooplaAccessLevel";
import { ZooplaListingsDTO } from "react-migration/domains/comparables/typings/Zoopla";
import { ComparableImagePreview } from "./components/ComparableImagePreview";
import { ComparableDetailHeader } from "./components/ComparableDetailHeader";
import { ComparablePriceSection } from "./components/ComparablePriceSection";
import { IComparablesTransactionDTO } from "react-migration/lib/typings/comparables/Comparables";
import { ComparableItemTransactionHistory } from "./components/ComparableItemTransactionHistory";
import { ComparablePropertyAttributes } from "./components/ComparablePropertyAttributes";
import { ComparableDetailSourceCard } from "./components/ComparableDetailSourceCard";
import { PriceSource } from "react-migration/domains/comparables/hooks/useComparableDetailData";
import { ZooplaAddress } from "@gql/federated-api/graphql";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";
export interface ComparableItemDetailProps {
  record?: IComparablesTransactionDTO;
  recordLoading?: boolean;
  zooplaData?: ZooplaComparable;
  zooplaDataLoading?: boolean;
  listingsData?: ZooplaListingsDTO;
  listingsLoading?: boolean;
  unitPreference: MeasurementSystem;
  locale: SupportedLocale;
  isZoopla: boolean;
}

export const ComparableItemDetail: FC<ComparableItemDetailProps> = ({
  record,
  recordLoading,
  zooplaData,
  zooplaDataLoading,
  listingsData,
  listingsLoading,
}) => {
  const accessLevel = getZooplaAccessLevel();
  const noFullAccess = accessLevel !== "full";
  const isUSUser = hasFeature(Feature.usAccess);

  const zooplaAddressString = zooplaData?.location?.address
    ? ["propertyNumberOrName", "streetName", "locality", "townOrCity", "postcode"]
        .map((key) =>
          getAddressDetail(
            zooplaData.location.address[key as keyof Omit<ZooplaAddress, "__typename">],
            key === "postcode"
          )
        )
        .filter(Boolean)
        .join(", ")
    : null;
  const address = zooplaAddressString ?? record?.address ?? "N/A";

  return (
    <Section data-testid="comparable-item-detail" bodyClasses="flex flex-col p-[10px] gap-y-4 gap-x-2 relative">
      {!isUSUser && (
        <ComparableImagePreview
          noFullAccess={noFullAccess}
          listingsData={listingsData}
          loading={listingsLoading}
          address={address}
        />
      )}
      <ComparableDetailHeader
        record={record}
        zooplaData={zooplaData}
        zooplaListing={listingsData}
        loading={zooplaDataLoading || recordLoading}
        address={address}
        noFullAccess={noFullAccess}
      />
      <ComparablePriceSection
        record={record}
        zooplaData={zooplaData}
        loading={zooplaDataLoading || recordLoading}
        noFullAccess={noFullAccess}
        listingsData={listingsData}
      />
      <ComparableItemTransactionHistory
        record={record}
        loading={recordLoading || zooplaDataLoading}
        zooplaData={zooplaData}
        noFullAccess={noFullAccess}
      />
      <ComparablePropertyAttributes
        record={record}
        zooplaData={zooplaData}
        loading={zooplaDataLoading || recordLoading}
        accessLevel={accessLevel}
      />
      {!isUSUser && <ComparableDetailSourceCard source={noFullAccess ? PriceSource.HMLR : PriceSource.HOMETRACK} />}
    </Section>
  );
};

function getAddressDetail(detail: string | null | undefined, isPostcode: boolean) {
  if (!detail) return null;
  return isPostcode ? detail.toUpperCase() : detail.toLowerCase();
}
